
import { supabase } from "@/integrations/supabase/client";
import { BlogPost } from "@/types/blog";
import { toast } from "@/hooks/use-toast";
import { diagnoseDatabase } from "./diagnostics";

/**
 * Import a single blog post to the database
 */
export const importToDatabase = async (post: BlogPost): Promise<void> => {
  try {
    console.log("Importing post to database:", post.title);
    
    // Check if the post already exists in the database (by WordPress ID or slug)
    let query = supabase
      .from("blog_posts")
      .select("id, wordpress_id, slug");

    if (post.wordpressId) {
      query = query.eq("wordpress_id", parseInt(post.wordpressId) || 0);
    } else {
      query = query.eq("slug", post.slug);
    }

    const { data: existingPosts, error: findError } = await query;
    
    if (findError) {
      console.error("Error checking for existing post:", findError);
      throw new Error(`Database error: ${findError.message}`);
    }
    
    const existingPost = existingPosts && existingPosts.length > 0 ? existingPosts[0] : null;
    
    // Prepare the database record - only include fields that actually exist in the database
    const blogRecord = {
      title: post.title,
      slug: post.slug,
      content: post.content,
      post_summary: post.excerpt,
      featured_image: post.coverImage,
      author: post.author || "edgefi team",
      publish_date: post.originalDate || post.date,
      is_published: true,
      wordpress_id: post.wordpressId ? parseInt(post.wordpressId) : null,
      meta_description: post.metaDescription || post.excerpt?.substring(0, 160),
      seo_title: post.title
    };
    
    // Log what we're about to insert/update
    console.log(existingPost ? "Updating existing post" : "Inserting new post");
    console.log("Blog record:", JSON.stringify(blogRecord, null, 2));
    
    let result;
    let postId: number;
    
    if (existingPost) {
      // Update existing post
      console.log(`Updating existing post with ID: ${existingPost.id}`);
      result = await supabase
        .from("blog_posts")
        .update(blogRecord)
        .eq("id", existingPost.id);
        
      postId = existingPost.id;
    } else {
      // Insert new post
      console.log("Inserting new blog post");
      result = await supabase.from("blog_posts").insert(blogRecord).select();
      
      if (result.data && result.data.length > 0) {
        postId = result.data[0].id;
        console.log(`Inserted new post with ID: ${postId}`);
      }
    }
    
    if (result.error) {
      console.error("Error saving post to database:", result.error);
      throw new Error(`Database error: ${result.error.message}`);
    }
    
    // If we have tags, let's add them to the database
    if (post.tags && Array.isArray(post.tags) && post.tags.length > 0 && postId) {
      console.log(`Processing ${post.tags.length} tags for post ID ${postId}`);
      
      // First, remove existing tag associations to prevent duplicates
      if (existingPost) {
        const { error: deleteError } = await supabase
          .from("blog_post_tags")
          .delete()
          .eq("post_id", postId);
          
        if (deleteError) {
          console.error("Error removing existing tag associations:", deleteError);
        }
      }
      
      // Then check if tags exist or create them
      for (const tagName of post.tags) {
        // Skip null/undefined/empty tags
        if (!tagName || typeof tagName !== 'string') {
          console.log("Skipping invalid tag:", tagName);
          continue;
        }
        
        // Ensure tag name is a string and trim it
        try {
          const normalizedTagName = tagName.trim();
          if (normalizedTagName === '') {
            console.log("Skipping empty tag after trimming");
            continue;
          }
          
          // Check if tag exists
          const { data: existingTags } = await supabase
            .from("blog_tags")
            .select("id, name")
            .eq("name", normalizedTagName);
            
          let tagId: number;
          
          if (existingTags && existingTags.length > 0) {
            // Tag exists, use its ID
            tagId = existingTags[0].id;
            console.log(`Using existing tag: ${normalizedTagName} (ID: ${tagId})`);
          } else {
            // Create new tag
            console.log(`Creating new tag: ${normalizedTagName}`);
            const { data: newTag, error } = await supabase
              .from("blog_tags")
              .insert({ name: normalizedTagName })
              .select();
              
            if (error) {
              console.error(`Error creating tag "${normalizedTagName}":`, error);
              continue;
            }
            
            if (newTag && newTag.length > 0) {
              tagId = newTag[0].id;
              console.log(`Created new tag: ${normalizedTagName} (ID: ${tagId})`);
            } else {
              console.error(`Failed to create tag: ${normalizedTagName}`);
              continue;
            }
          }
          
          // Link tag to post
          console.log(`Linking tag ${normalizedTagName} (ID: ${tagId}) to post ID ${postId}`);
          const { error: linkError } = await supabase
            .from("blog_post_tags")
            .insert({
              post_id: postId,
              tag_id: tagId
            });
            
          if (linkError) {
            // If it's a unique constraint error, the tag is already linked
            if (!linkError.message.includes('unique constraint')) {
              console.error(`Error linking tag "${normalizedTagName}" to post:`, linkError);
            } else {
              console.log(`Tag "${normalizedTagName}" already linked to post`);
            }
          } else {
            console.log(`Successfully linked tag "${normalizedTagName}" to post`);
          }
        } catch (tagError) {
          console.error(`Error processing tag ${tagName}:`, tagError);
          // Continue with next tag
        }
      }
    }
    
    // Verify insertion with immediate fetch check
    const verifyQuery = post.wordpressId 
      ? supabase.from("blog_posts").select("id, title").eq("wordpress_id", parseInt(post.wordpressId))
      : supabase.from("blog_posts").select("id, title").eq("slug", post.slug);
    
    const { data: verifyData, error: verifyError } = await verifyQuery;
    
    if (verifyError) {
      console.error("Error verifying post insertion:", verifyError);
    } else {
      console.log("Verification result:", verifyData);
      if (!verifyData || verifyData.length === 0) {
        console.warn("WARNING: Post may not have been saved correctly! Could not verify in database.");
      }
    }
    
    console.log(`Successfully ${existingPost ? "updated" : "imported"} post: ${post.title}`);
  } catch (error) {
    console.error("Error in importToDatabase:", error);
    throw error;
  }
};

/**
 * Import all blog posts to the database
 */
export const importAllToDatabase = async (posts: BlogPost[]): Promise<{successCount: number, errorCount: number, diagnosticResults?: any}> => {
  let successCount = 0;
  let errorCount = 0;
  
  console.log(`Starting import of ${posts.length} posts...`);

  // Run pre-import diagnostics to have a baseline
  console.log("Running pre-import database diagnostics...");
  const preDiagnostics = await diagnoseDatabase();
  console.log("Pre-import database state:", preDiagnostics);
  
  for (const [index, post] of posts.entries()) {
    try {
      // Show progress for large imports
      if (index % 10 === 0) {
        toast({
          title: "Import Progress",
          description: `Importing posts: ${index + 1} of ${posts.length}`,
        });
      }
      
      console.log(`Importing post ${index + 1}/${posts.length}: "${post.title}"`);
      await importToDatabase(post);
      successCount++;
    } catch (error) {
      console.error(`Failed to import post "${post.title}":`, error);
      errorCount++;
      
      // Show toast only for the first few errors to avoid spamming
      if (errorCount <= 3) {
        toast({
          title: `Import Failed: ${post.title}`,
          description: error instanceof Error ? error.message : "Unknown error",
          variant: "destructive"
        });
      }
    }
  }
  
  // If we've suppressed some error toasts, show a summary
  if (errorCount > 3) {
    toast({
      title: "Multiple Import Failures",
      description: `${errorCount - 3} additional posts failed to import. Check console for details.`,
      variant: "destructive"
    });
  }
  
  // Run diagnostics after import to verify data
  const diagnosticResults = await diagnoseDatabase();
  console.log("Post-import database diagnostics:", diagnosticResults);

  // Compare pre and post import to verify changes
  const preCount = preDiagnostics?.postsCount || 0;
  const postCount = diagnosticResults?.postsCount || 0;
  console.log(`Database post count - Before: ${preCount}, After: ${postCount}, Difference: ${postCount - preCount}`);
  
  if (successCount > 0 && postCount <= preCount) {
    console.warn("WARNING: Import may have failed! Post count in database did not increase as expected.");
  }
  
  return { successCount, errorCount, diagnosticResults };
};

/**
 * Purge all blog posts from the database
 * This is a destructive action and should be used with caution
 */
export const purgeAllBlogPosts = async (): Promise<{success: boolean, message: string}> => {
  try {
    console.log("Purging all blog posts from database");
    
    // First, clear the post-tag relationships
    const { error: tagRelError } = await supabase
      .from("blog_post_tags")
      .delete()
      .not("post_id", "is", null);
      
    if (tagRelError) {
      console.error("Error purging blog post tags:", tagRelError);
    }
    
    // Then delete all blog posts
    const { error } = await supabase
      .from("blog_posts")
      .delete()
      .not("id", "is", null); // Safety check to ensure we have a valid condition
    
    if (error) {
      console.error("Error purging blog posts:", error);
      return {
        success: false,
        message: `Failed to delete blog posts: ${error.message}`
      };
    }
    
    console.log("Successfully purged all blog posts");
    return {
      success: true,
      message: "All blog posts have been successfully deleted"
    };
  } catch (error) {
    console.error("Error in purgeAllBlogPosts:", error);
    return {
      success: false,
      message: error instanceof Error ? error.message : "Unknown error occurred"
    };
  }
};
